import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, Flame, FileText } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import type { AssignmentDashboardStats } from '../hooks/useAssignmentDashboardStats';
import type { StudentProgressAnalyticsOut } from '../../tutor/api/tutor.client';
import { lessonScorePercent } from '../../../components/student/studentLessonProgressFriendly';
import { KpiCard } from './KpiCard';
import { DashboardInlineError } from './dashboardUi';

type Props = {
  tutorAnalytics: TutorAnalyticsSummary | null;
  tutorLoading: boolean;
  tutorEnabled: boolean;
  tutorModuleTitle: string;
  practiceStreak: number;
  streakLoading: boolean;
  streakEnabled: boolean;
  progressAnalytics: StudentProgressAnalyticsOut | null;
  progressLoading: boolean;
  progressEnabled: boolean;
  assignmentStats: AssignmentDashboardStats;
  assignmentLoading: boolean;
  assignmentEnabled: boolean;
  assignmentError?: string | null;
  onRetryAssignment?: () => void;
};

function formatMinutes(minutes: number): string {
  const value = Math.round(minutes * 10) / 10;
  if (value <= 0) return '—';
  return `${value}m`;
}

function countCompletedLessons(data: StudentProgressAnalyticsOut | null): number {
  if (!data?.lessons?.length) return 0;
  return data.lessons.filter((row) => {
    const pct = lessonScorePercent(row.overall_score);
    return pct != null && pct >= 75;
  }).length;
}

export function LearningOverview({
  tutorAnalytics,
  tutorLoading,
  tutorEnabled,
  tutorModuleTitle,
  practiceStreak,
  streakLoading,
  streakEnabled,
  progressAnalytics,
  progressLoading,
  progressEnabled,
  assignmentStats,
  assignmentLoading,
  assignmentEnabled,
  assignmentError,
  onRetryAssignment,
}: Props) {
  const { t } = useTranslation();

  const completedLessons = useMemo(
    () => countCompletedLessons(progressAnalytics),
    [progressAnalytics],
  );

  const showLearningTime = tutorEnabled;
  const showStreak = streakEnabled;
  const showLessons = progressEnabled;
  const showHomework = assignmentEnabled;

  if (!showLearningTime && !showStreak && !showLessons && !showHomework) {
    return null;
  }

  const homeworkSubtitle = (() => {
    if (assignmentStats.total === 0) return t('dashboard.studentHome.noAssignmentsYet');
    const parts = [
      t('dashboard.studentHome.submittedCount', { count: assignmentStats.submitted }),
      t('dashboard.studentHome.pendingCount', { count: assignmentStats.pending }),
    ];
    if (assignmentStats.overdue > 0) {
      parts.push(t('dashboard.studentHome.overdueCount', { count: assignmentStats.overdue }));
    }
    return parts.join(' · ');
  })();

  const visibleCardsCount = [showLearningTime, showStreak, showLessons, showHomework].filter(Boolean).length;

  const gridColsClass = (() => {
    if (visibleCardsCount === 1) return 'grid-cols-1';
    if (visibleCardsCount === 2) return 'grid-cols-1 md:grid-cols-2';
    if (visibleCardsCount === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4';
  })();

  return (
    <section className="mb-6" aria-label={t('dashboard.studentHome.learningOverview')}>
      <h2 className="sr-only">{t('dashboard.studentHome.learningOverview')}</h2>
      <div className={`grid ${gridColsClass} gap-4`}>
        {showLearningTime ? (
          <KpiCard
            icon={Clock}
            label={t('dashboard.studentHome.learningTime')}
            value={
              tutorAnalytics && tutorAnalytics.total_tutor_minutes > 0
                ? formatMinutes(tutorAnalytics.total_tutor_minutes)
                : '—'
            }
            subtitle={t('dashboard.studentHome.learningTimeSubtitle', { tutor: tutorModuleTitle })}
            loading={tutorLoading}
            accentClass="from-indigo-500 to-violet-600"
          />
        ) : null}
        {showStreak ? (
          <KpiCard
            icon={Flame}
            label={t('dashboard.studentHome.practiceStreak')}
            value={practiceStreak > 0 ? practiceStreak : '—'}
            subtitle={
              practiceStreak > 0
                ? t('dashboard.studentHome.streakActive')
                : t('dashboard.studentHome.streakEmpty')
            }
            loading={streakLoading}
            accentClass="from-orange-500 to-amber-500"
          />
        ) : null}
        {showLessons ? (
          <KpiCard
            icon={BookOpen}
            label={t('dashboard.studentHome.completedLessons')}
            value={completedLessons > 0 ? completedLessons : '—'}
            subtitle={t('dashboard.studentHome.completedLessonsSubtitle')}
            loading={progressLoading}
            accentClass="from-emerald-500 to-teal-600"
          />
        ) : null}
        {showHomework ? (
          assignmentError && assignmentStats.total === 0 ? (
            <Card className="border-purple-200/60 dark:border-purple-500/25 sm:col-span-2 xl:col-span-1">
              <CardContent className="p-4 sm:p-5">
                <DashboardInlineError message={assignmentError} onRetry={onRetryAssignment} />
              </CardContent>
            </Card>
          ) : (
            <KpiCard
              icon={FileText}
              label={t('dashboard.studentHome.homework')}
              value={assignmentStats.total > 0 ? assignmentStats.total : '—'}
              subtitle={homeworkSubtitle}
              loading={assignmentLoading}
              accentClass="from-purple-500 to-fuchsia-600"
            />
          )
        ) : null}
      </div>
    </section>
  );
}
